import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { FilterChips } from "@/components/FilterChips";
import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/Skeleton";
import { ProjectCard, type ProjectCardProject } from "@/components/ProjectCard";
import { ProjectRow } from "@/components/ProjectRow";

type View = "grid" | "list";
type Filter =
  | "all"
  | "active"
  | "on_hold"
  | "standing"
  | "initiative"
  | "archived";

const VIEW_OPTIONS = [
  { value: "grid" as const, label: "Grid", icon: "board" as const },
  // No dedicated "list" glyph in the Icon set; "clip" is the closest
  // visual match used elsewhere in the app for list views.
  { value: "list" as const, label: "List", icon: "clip" as const },
];

const FILTER_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "active" as const, label: "Active" },
  { value: "on_hold" as const, label: "On Hold" },
  { value: "standing" as const, label: "Standing Projects" },
  { value: "initiative" as const, label: "Initiatives" },
  { value: "archived" as const, label: "Archived" },
];

function ProjectsIndex() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>("grid");
  const [filter, setFilter] = useState<Filter>("all");

  const q = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });

  const goToNewProject = () => void navigate({ to: "/projects/new" });

  const all = (q.data?.projects ?? []) as ProjectCardProject[];

  const items = all.filter((p) => {
    if (filter === "all") return true;
    if (filter === "standing") return p.kind === "standing";
    if (filter === "initiative") return p.kind === "initiative";
    return p.status === filter;
  });

  // Match designs/Projects.html: header count reflects the *filtered* list.
  const visibleCount = q.data ? items.length : 0;

  return (
    <div className="pg-wrap">
      {/* Page header */}
      <header className="pg-head">
        <h1>Projects</h1>
        {q.data ? <span className="count">{visibleCount}</span> : null}
        <div className="right">
          <SegmentedToggle
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
          <Button variant="primary" size="sm" onClick={goToNewProject}>
            <Icon name="plus" size={14} /> New project
          </Button>
        </div>
      </header>

      {/* Filter chips */}
      <FilterChips
        options={FILTER_OPTIONS}
        value={filter}
        onChange={setFilter}
      />

      {/* Content: loading → error → empty → grid/list (Dashboard pattern) */}
      {q.isLoading ? (
        <Skeleton count={6} height={120} />
      ) : q.isError ? (
        <p
          className="t-sm"
          style={{ padding: "16px 20px", color: "var(--st-danger-ink)" }}
        >
          Could not load projects.
        </p>
      ) : !q.data || items.length === 0 ? (
        <EmptyState
          icon="folder"
          title="No projects yet"
          caption="Spin up your first project."
          actionLabel="New project"
          onAction={goToNewProject}
        />
      ) : view === "grid" ? (
        <div className="grid-view">
          {items.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      ) : (
        <div className="list-view">
          {/* Header row */}
          <div className="lrow lhead">
            <div>Project</div>
            <div>Status</div>
            <div>Progress</div>
            <div>Team</div>
            <div>Due</div>
          </div>
          {items.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/")({
  component: ProjectsIndex,
});
